import React, { useState } from 'react';
import { formatRut, formatMoneyWithCurrency, formatMoney } from '../../../utils/formatter';
import { documentsDic } from '../../../utils/dataDictionary';
import { CurrencyIcon } from 'src/utils/currencyIcon';
import ExportActions from '../../export/ExportActions';
import moment from 'moment';
import FilterTabs from '../../FilterTabs/FilterTabs';
import CommentsTab from './CommentsTab';
import ParametrizacionTab from './ParametrizacionTab';
import { dt } from 'src/utils/documentsI18n';

const InvoicesDetail = ({ data }) => {
  const [activeTab, setActiveTab] = useState('detalles');
  const isReceived = data.meta.isReceived;
  const commentCount = data.meta?.innerComments?.length || 0;
  
  const tabs = [
    { key: 'detalles', label: dt('detail.tabs.details') },
    { key: 'parametrizacion', label: dt('detail.tabs.settings') },
    { key: 'comentarios', label: commentCount > 0 ? `${dt('detail.tabs.comments')} (${commentCount})` : dt('detail.tabs.comments') }
  ];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'detalles':
        return (
          <div className="tw-space-y-4">
      {/* Document Header - Compact */}
      <div className="tw-bg-orange-50 tw-p-3 tw-rounded-lg tw-border-l-4 tw-border-orange-400">
        <div className="tw-flex tw-justify-between tw-items-center">
          <div>
            <h3 className="tw-text-lg tw-font-bold tw-text-gray-800 tw-mb-1">
              {documentsDic[parseInt(data.meta.documentCode)]?.fullName || data.meta.summary}
            </h3>
          </div>
          <div className="tw-text-right">
            <div className="tw-text-xl tw-font-bold tw-text-orange-600">
              {`${CurrencyIcon[data.currencyType]} ` +
                formatMoney(
                  [0, "0", 1, "1"].includes(data.meta?.documentCode)
                    ? data.meta.netAmount
                    : data.meta.originalAmount || 0, 
                  data.currencyType == 'CLP' ? 'CLP' : 'USD', 
                  true
                )}
            </div>
            <div className="tw-text-xs tw-text-gray-500">
              Folio: {data.meta.documentId || data.meta.number || "s/i"}
            </div>
          </div>
        </div>
      </div>

      {/* Company and Dates - Side by side */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-4">
        <div className="tw-bg-white tw-p-3 tw-rounded-lg tw-border tw-border-gray-200">
          <h4 className="tw-font-semibold tw-text-gray-800 tw-mb-2 tw-text-sm tw-flex tw-items-center">
            <span className="tw-w-2 tw-h-2 tw-bg-orange-400 tw-rounded-full tw-mr-2"></span>
            {isReceived ? 'Emisor' : 'Receptor'}
          </h4>
          <div className="tw-space-y-1">
            <div>
              <span className="tw-text-xs tw-text-gray-500">{dt('detail.businessName')}:</span>
              <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
                {isReceived ? data.meta.issuerName || "SIN CONTRAPARTE" : data.meta.receiverName || "SIN CONTRAPARTE"}
              </p>
            </div>
            <div>
              <span className="tw-text-xs tw-text-gray-500">RUT:</span>
              <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
                {formatRut(isReceived ? data.meta.issuerId : data.meta.receiverId) || "-"}
              </p>
            </div>
          </div>
        </div>

        <div className="tw-bg-white tw-p-3 tw-rounded-lg tw-border tw-border-gray-200">
          <h4 className="tw-font-semibold tw-text-gray-800 tw-mb-2 tw-text-sm tw-flex tw-items-center">
            <span className="tw-w-2 tw-h-2 tw-bg-blue-400 tw-rounded-full tw-mr-2"></span>
            Fechas
          </h4>
          <div className="tw-space-y-1">
            <div>
              <span className="tw-text-xs tw-text-gray-500">{dt('detail.issueShort')}:</span>
              <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
                {moment(data.documentDate).format("DD/MM/YYYY")}
              </p>
            </div>
            <div>
              <span className="tw-text-xs tw-text-gray-500">Vencimiento:</span>
              <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
                {data.originalDueDate ? moment(data.originalDueDate).format("DD/MM/YYYY") : 's/i'}
              </p>
            </div>
            <div>
              <span className="tw-text-xs tw-text-gray-500">Pago:</span>
              <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
                {data?.meta?.isPaid === true && data.paymentDate ? moment(data.paymentDate.substr(0, 10)).format("DD/MM/YYYY") : 's/i'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Amount Details - Compact grid */}
      <div className="tw-bg-white tw-p-3 tw-rounded-lg tw-border tw-border-gray-200">
        <h4 className="tw-font-semibold tw-text-gray-800 tw-mb-2 tw-text-sm tw-flex tw-items-center">
          <span className="tw-w-2 tw-h-2 tw-bg-purple-400 tw-rounded-full tw-mr-2"></span>
          Detalles de Montos
        </h4>
        <div className="tw-grid tw-grid-cols-2 tw-gap-3">
          {/* Exchange information - show first if exists and originalCurrency is not CLP */}
          {data.meta?.exchange?.originalCurrency && data.meta.exchange.originalCurrency !== 'CLP' && (
            <>
              <div>
                <span className="tw-text-xs tw-text-gray-500">MONTO MONEDA ORIGINAL ({data.meta.exchange.originalCurrency}):</span>
                <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
                  {`${CurrencyIcon[data.meta.exchange.originalCurrency] || ''} ` +
                    formatMoney(
                      data.meta.exchange.originalAmount || 0, 
                      data.meta.exchange.originalCurrency, 
                      true
                    )}
                </p>
              </div>
              {data.meta.exchange.exchangeRate && (
                <div>
                  <span className="tw-text-xs tw-text-gray-500">TIPO DE CAMBIO:</span>
                  <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
                    {data.meta.exchange.exchangeRate}
                  </p>
                </div>
              )}
            </>
          )}
          {/* Hide Monto Original if exchange exists */}
          {!(data.meta?.exchange?.originalCurrency && data.meta.exchange.originalCurrency !== 'CLP') && (
            <div>
              <span className="tw-text-xs tw-text-gray-500">{dt('detail.originalAmount')}:</span>
              <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
                {`${CurrencyIcon[data.currencyType]} ` +
                  formatMoney(
                    data.meta.originalAmount || 0, 
                    data.currencyType == 'CLP' ? 'CLP' : 'USD', 
                    true
                  )}
              </p>
            </div>
          )}
          <div>
            <span className="tw-text-xs tw-text-gray-500">{dt('detail.netAmount')}:</span>
            <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
              {`${CurrencyIcon[data.currencyType]} ` +
                formatMoney(
                  data.meta.netAmount || 0, 
                  data.currencyType == 'CLP' ? 'CLP' : 'USD', 
                  true
                )}
            </p>
          </div>
          <div>
            <span className="tw-text-xs tw-text-gray-500">IVA:</span>
            <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
              {`${CurrencyIcon[data.currencyType]} ` +
                formatMoney(
                  data.meta?.taxAmount || 0, 
                  data.currencyType == 'CLP' ? 'CLP' : 'USD', 
                  true
                )}
            </p>
          </div>
          <div>
            <span className="tw-text-xs tw-text-gray-500">Remanente:</span>
            <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
              {`${CurrencyIcon[data.currencyType]} ` +
                formatMoney(
                  data.meta.remaining || 0, 
                  data.currencyType == 'CLP' ? 'CLP' : 'USD', 
                  true
                )}
            </p>
          </div>
          {/* Show MONTO (CLP) if exchange exists */}
          {data.meta?.exchange?.originalCurrency && data.meta.exchange.originalCurrency !== 'CLP' && (
            <div>
              <span className="tw-text-xs tw-text-gray-500">MONTO (CLP):</span>
              <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
                {`${CurrencyIcon['CLP'] || ''} ` +
                  formatMoney(
                    data.meta.exchange.localAmount || 0, 
                    'CLP', 
                    true
                  )}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Status and Additional Info - Side by side */}
      <div className="tw-grid tw-grid-cols-2 tw-gap-4">
        <div className="tw-bg-white tw-p-3 tw-rounded-lg tw-border tw-border-gray-200">
          <h4 className="tw-font-semibold tw-text-gray-800 tw-mb-2 tw-text-sm tw-flex tw-items-center">
            <span className="tw-w-2 tw-h-2 tw-bg-yellow-400 tw-rounded-full tw-mr-2"></span>
            Estado
          </h4>
          <div className="tw-flex tw-items-center tw-gap-2">
            <div className={`tw-px-2 tw-py-1 tw-rounded-full tw-text-xs tw-font-medium ${
              data.meta.isPaid 
                ? 'tw-bg-green-100 tw-text-green-800' 
                : data.meta?.isValid === false 
                  ? 'tw-bg-red-100 tw-text-red-800' 
                  : 'tw-bg-yellow-100 tw-text-yellow-800'
            }`}>
              {data.meta?.isValid === false 
                ? 'ANULADA'
                : data.meta.isPaid 
                  ? 'PAGADO'
                  : 'PENDIENTE'
              }
            </div>
          </div>
        </div>

        <div className="tw-bg-white tw-p-3 tw-rounded-lg tw-border tw-border-gray-200">
          <h4 className="tw-font-semibold tw-text-gray-800 tw-mb-2 tw-text-sm tw-flex tw-items-center">
            <span className="tw-w-2 tw-h-2 tw-bg-indigo-400 tw-rounded-full tw-mr-2"></span>
            Información
          </h4>
          <div className="tw-space-y-1">
            <div>
              <span className="tw-text-xs tw-text-gray-500">{dt('detail.siiCode')}:</span>
              <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
                {data.meta.documentCode || "s/i"}
              </p>
            </div>
            <div>
              <span className="tw-text-xs tw-text-gray-500">{dt('detail.typeShort')}:</span>
              <p className="tw-font-medium tw-text-gray-800 tw-text-sm">
                {isReceived ? 'Recibido' : 'Emitido'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Export Actions */}
      <div className="tw-mt-4">
        <ExportActions
          documentData={data}
          exportType="rendiciones"
          pdfUrl={data.meta.download?.pdf?.url || data.meta.download?.html?.url}
          xmlUrl={data.meta.download?.xml?.url}
          showPdf={!!(data.meta.download?.pdf?.url || data.meta.download?.html?.url)}
          showXml={!!data.meta.download?.xml?.url}
          showExcel={true}
        />
      </div>
        </div>
      );
      case 'parametrizacion':
        return <ParametrizacionTab data={data} />;
      case 'comentarios':
        return <CommentsTab data={data} />;
      default:
        return null;
    }
  };

  return (
    <div className="document-detail-container">
      <FilterTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="document-filter-tabs"
      />
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default InvoicesDetail; 